import React, { useState } from "react";
import Papa from "papaparse";
import * as XLSX from 'xlsx';
import { db, storage, auth } from "./firebase";
import { ref, uploadBytes, deleteObject } from "firebase/storage";
import {
  collection, addDoc, serverTimestamp, query, where,
  getDocs, updateDoc, doc, setDoc, writeBatch, deleteDoc
} from "firebase/firestore";

function AdminUpload() {
  const [dataType, setDataType] = useState("specialty"); // "specialty" or "observership"
  const [specialty, setSpecialty] = useState("");
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [deleteSpecialty, setDeleteSpecialty] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");

  const handleDeleteSpecialty = async () => {
    const name = deleteSpecialty.trim();
    if (!name) { setDeleteMessage("⚠️ Enter a specialty name to delete."); return; }
    if (!window.confirm(`Delete ALL data for "${name}"? This cannot be undone.`)) return;
    setIsDeleting(true);
    setDeleteMessage("⏳ Deleting…");
    try {
      const key = name.toLowerCase().replace(/\s+/g, "_");
      // Delete Firestore programs subcollection
      const programsRef = collection(db, "specialtyData", key, "programs");
      const snap = await getDocs(programsRef);
      const BATCH = 400;
      for (let i = 0; i < snap.docs.length; i += BATCH) {
        const b = writeBatch(db);
        snap.docs.slice(i, i + BATCH).forEach(d => b.delete(d.ref));
        await b.commit();
      }
      // Delete meta/fields doc
      try { await deleteDoc(doc(db, "specialtyData", key, "meta", "fields")); } catch (_) {}
      // Delete specialty metadata doc from specialties collection
      const q = query(collection(db, "specialties"), where("name", "==", name));
      const specSnap = await getDocs(q);
      specSnap.forEach(async d => { try { await deleteDoc(d.ref); } catch (_) {} });
      // Delete from Firebase Storage
      try { await deleteObject(ref(storage, `specialties/${key}.csv`)); } catch (_) {}
      // Clear localStorage cache
      localStorage.removeItem(`table_${name}_v2`);
      localStorage.removeItem(`table_${key}_v2`);
      setDeleteMessage(`✅ "${name}" deleted successfully (${snap.docs.length} programs removed).`);
      setDeleteSpecialty("");
    } catch (err) {
      setDeleteMessage(`❌ Error: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

const handleUpload = async (e) => {
    e.preventDefault();

    if (dataType === "specialty" && !specialty.trim()) {
      setMessage("⚠️ Please enter a specialty name.");
      return;
    }

    if (!file) {
      setMessage("⚠️ Please select a file to upload.");
      return;
    }

    if ((dataType === "specialty" && !file.name.toLowerCase().endsWith('.csv')) ||
        (dataType === "observership" && !file.name.toLowerCase().match(/\.(xlsx|xls)$/))) {
      setMessage(`⚠️ Please select a valid ${dataType === "specialty" ? "CSV" : "Excel"} file.`);
      return;
    }

    setIsUploading(true);
    setMessage("");

    let fileName, fileRef, collectionName;


    if (dataType === "specialty") {
      fileName = specialty.toLowerCase().replace(/\s+/g, "_") + ".csv";
      fileRef = ref(storage, `specialties/${fileName}`);
      collectionName = `specialties`;
    } else {
      fileName = "observerships.xlsx"; // Fixed name for observerships
      fileRef = ref(storage, `observerships/${fileName}`);
      collectionName = `observerships`;
    }

    try {
      // Upload to Firebase Storage
      await uploadBytes(fileRef, file);

      if (dataType === "specialty") {
        // This part correctly uses the prefixed 'collectionName' from above
        const specialtyQuery = query(
          collection(db, collectionName), 
          where("name", "==", specialty.trim())
        );
        const existingSpecialties = await getDocs(specialtyQuery);

        if (existingSpecialties.empty) {
          await addDoc(collection(db, collectionName), {
            name: specialty.trim(),
            timestamp: new Date(),
            fileName: fileName,
            fileSize: file.size,
            uploadedBy: auth.currentUser?.email || "Unknown"
          });
        } else {
          const specialtyDoc = existingSpecialties.docs[0];
          await updateDoc(doc(db, collectionName, specialtyDoc.id), {
            timestamp: new Date(),
            fileName: fileName,
            fileSize: file.size,
            uploadedBy: auth.currentUser?.email || "Unknown",
            updatedAt: new Date()
          });
        }

       
        await addDoc(collection(db, `uploads`), {
          filename: fileName,
          specialty: specialty.trim(),
          uploadedBy: auth.currentUser?.email || "Unknown",
          date: serverTimestamp(),
          fileSize: file.size,
          status: "success",
          action: existingSpecialties.empty ? "created" : "updated",
          dataType: "specialty"
        });

        // Bust user caches by writing a new version timestamp
        await setDoc(doc(db, "dataVersions", specialty.trim().toLowerCase().replace(/\s+/g, "_")), {
          updatedAt: Date.now()
        });

        // ── Write every CSV row to Firestore for instant paginated reads ──────
        // Collection: specialtyData/{key}/programs/{auto-id}
        // Each document = one CSV row + _idx for ordering.
        const specialtyKey = specialty.trim().toLowerCase().replace(/\s+/g, "_");
        const programsColRef = collection(db, "specialtyData", specialtyKey, "programs");

        // Parse the CSV from the uploaded file
        const csvText = await file.text();
        const parsed = Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          transformHeader: h => h.replace(/^\ufeff/, '').trim()
        });
        // Remove columns whose header is empty (e.g. trailing commas in CSV)
        const validHeaders = (parsed.meta.fields || []).filter(h => h.trim() !== '');
        const rows = parsed.data
          .filter(row => Object.values(row).some(cell => cell?.toString().trim()))
          .map(row => {
            // Keep only valid header keys; replace null/undefined with empty string
            const clean = {};
            validHeaders.forEach(h => { clean[h] = row[h] ?? ''; });
            return clean;
          });

        // Delete all existing documents in the subcollection
        setMessage("⏳ Clearing old data from Firestore…");
        const existingSnap = await getDocs(programsColRef);
        const DEL_BATCH_SIZE = 400;
        for (let i = 0; i < existingSnap.docs.length; i += DEL_BATCH_SIZE) {
          const delBatch = writeBatch(db);
          existingSnap.docs.slice(i, i + DEL_BATCH_SIZE).forEach(d => delBatch.delete(d.ref));
          await delBatch.commit();
        }

        // Write new rows in batches of 400 (Firestore limit is 500)
        const WRITE_BATCH_SIZE = 400;
        const totalRows = rows.length;
        for (let i = 0; i < totalRows; i += WRITE_BATCH_SIZE) {
          const batch = writeBatch(db);
          rows.slice(i, i + WRITE_BATCH_SIZE).forEach((row, j) => {
            const docRef = doc(programsColRef);
            batch.set(docRef, { ...row, _idx: i + j });
          });
          await batch.commit();
          setMessage(`⏳ Saving rows to Firestore… ${Math.min(i + WRITE_BATCH_SIZE, totalRows)}/${totalRows}`);
        }

        // Store header order (valid headers only) in a meta document
        await setDoc(
          doc(db, "specialtyData", specialtyKey, "meta", "fields"),
          { headers: validHeaders }
        );

        setMessage(existingSpecialties.empty
          ? `✅ New specialty created! ${totalRows} programs saved to database.`
          : `✅ Specialty updated! ${totalRows} programs saved to database.`
        );
        // ─────────────────────────────────────────────────────────────────────
      } else {
        // This part correctly uses the prefixed 'collectionName' from above
        const observershipQuery = query(
          collection(db, collectionName), 
          where("type", "==", "observerships")
        );
        const existingObserverships = await getDocs(observershipQuery);

        if (existingObserverships.empty) {
          await addDoc(collection(db, collectionName), {
            type: "observerships",
            name: "US Clinical Observerships & Electives",
            timestamp: new Date(),
            fileName: fileName,
            fileSize: file.size,
            uploadedBy: auth.currentUser?.email || "Unknown"
          });
        } else {
          const observershipDoc = existingObserverships.docs[0];
          await updateDoc(doc(db, collectionName, observershipDoc.id), {
            timestamp: new Date(),
            fileName: fileName,
            fileSize: file.size,
            uploadedBy: auth.currentUser?.email || "Unknown",
            updatedAt: new Date()
          });
        }


        await addDoc(collection(db, `uploads`), {
          filename: fileName,
          type: "observerships",
          uploadedBy: auth.currentUser?.email || "Unknown",
          date: serverTimestamp(),
          fileSize: file.size,
          status: "success",
          action: existingObserverships.empty ? "created" : "updated",
          dataType: "observership"
        });

        setMessage(existingObserverships.empty 
          ? "✅ Observerships database created and uploaded successfully!" 
          : "✅ Observerships database updated successfully!"
        );

        // Bust user caches by writing a new version timestamp
        const uploadedAt = Date.now();
        await setDoc(doc(db, "dataVersions", "observerships"), {
          updatedAt: uploadedAt
        });

        // ── Parse Excel and write all rows to Firestore ──────────────────────
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, {
          cellStyles: false, cellFormulas: false, cellDates: false, cellNF: false
        });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Build row objects
        const obsRows = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || !row[1]) continue;
          obsRows.push({
            _idx: obsRows.length,
            id: String(row[0] || i),
            name: String(row[1] || ''),
            cityState: String(row[2] || ''),
            type: String(row[3] ? row[3].toString().replace(/\r\n/g, ' ').trim() : ''),
            specialty: String(row[4] || ''),
            personalContact: row[5] === 'Yes',
            website: String(row[6] || ''),
            contactPerson: String(row[7] || ''),
            contactEmail: String(row[8] || ''),
            requirements: String(row[9] || ''),
            fee: String(row[10] || 'Not specified'),
            duration: String(row[11] || ''),
            lastUpdated: String(row[12] || ''),
            notes: String(row[13] || '')
          });
        }

        // Delete all existing program docs
        const obsCol = collection(db, "observershipPrograms");
        const existingSnap = await getDocs(obsCol);
        const DEL_BATCH_SIZE = 400;
        for (let i = 0; i < existingSnap.docs.length; i += DEL_BATCH_SIZE) {
          const delBatch = writeBatch(db);
          existingSnap.docs.slice(i, i + DEL_BATCH_SIZE).forEach(d => delBatch.delete(d.ref));
          await delBatch.commit();
        }

        // Write new rows in batches of 400
        for (let i = 0; i < obsRows.length; i += DEL_BATCH_SIZE) {
          const writeBatchInst = writeBatch(db);
          obsRows.slice(i, i + DEL_BATCH_SIZE).forEach(rowObj => {
            writeBatchInst.set(doc(obsCol), rowObj);
          });
          await writeBatchInst.commit();
        }

        // Write filter meta — unique specialties, types, states for dropdowns
        const uniqueSpecialties = [...new Set(obsRows.map(r => r.specialty).filter(Boolean))].sort();
        const uniqueTypes = [...new Set(obsRows.map(r => r.type).filter(Boolean))].sort();
        const uniqueStates = [...new Set(obsRows.map(r => {
          if (r.cityState?.trim()) {
            const parts = r.cityState.split(',');
            if (parts.length > 1) return parts[parts.length - 1].trim().split(' ')[0];
          }
          return null;
        }).filter(Boolean))].sort();

        await setDoc(doc(db, "observershipMeta", "filters"), {
          specialties: uniqueSpecialties,
          types: uniqueTypes,
          states: uniqueStates,
          feeRanges: ['Free', '$1-500', '$501-1000', '$1001-2000', '$2001-3000', '$3000+'],
          totalCount: obsRows.length,
          updatedAt: uploadedAt
        });
        // ─────────────────────────────────────────────────────────────────────
      }

      setSpecialty("");
      setFile(null);
    } catch (error) {
      console.error("Upload error:", error);
      setMessage("❌ Upload failed. Please try again.");
      
      try {

        await addDoc(collection(db, `uploads`), {
          filename: fileName,
          specialty: dataType === "specialty" ? specialty.trim() : "observerships",
          uploadedBy: auth.currentUser?.email || "Unknown",
          date: serverTimestamp(),
          status: "failed",
          error: error.message,
          dataType: dataType
        });
      } catch (logError) {
        console.error("Error logging failed upload:", logError);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      const isValidFile = dataType === "specialty" ? 
        droppedFile.name.endsWith('.csv') : 
        (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls'));
      
      if (isValidFile) {
        setFile(droppedFile);
        setMessage("");
      } else {
        setMessage(`⚠️ Please drop a valid ${dataType === "specialty" ? "CSV" : "Excel"} file.`);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Upload Medical Data</h2>
        <p className="text-gray-600">Upload CSV files for specialty programs or Excel files for observerships</p>
      </div>

      {/* Data Type Selection */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <label className="block text-sm font-semibold text-gray-700 mb-4">
          Select Data Type
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => {
              setDataType("specialty");
              setFile(null);
              setMessage("");
            }}
            className={`p-4 rounded-xl border-2 transition-all duration-200 ${
              dataType === "specialty"
                ? "border-blue-500 bg-blue-50 text-blue-700"
                : "border-gray-200 bg-gray-50 text-gray-700 hover:border-blue-300 hover:bg-blue-50"
            }`}
          >
            <div className="text-center">
              <div className="text-2xl mb-2">🏥</div>
              <div className="font-semibold">Specialty Programs</div>
              <div className="text-sm mt-1">Upload CSV files with residency program data</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => {
              setDataType("observership");
              setFile(null);
              setMessage("");
              setSpecialty(""); // Clear specialty when switching
            }}
            className={`p-4 rounded-xl border-2 transition-all duration-200 ${
              dataType === "observership"
                ? "border-purple-500 bg-purple-50 text-purple-700"
                : "border-gray-200 bg-gray-50 text-gray-700 hover:border-purple-300 hover:bg-purple-50"
            }`}
          >
            <div className="text-center">
              <div className="text-2xl mb-2">🔬</div>
              <div className="font-semibold">Observerships</div>
              <div className="text-sm mt-1">Upload Excel files with observership opportunities</div>
            </div>
          </button>
        </div>
      </div>

      {/* Upload Form */}
      <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
        <form onSubmit={handleUpload} className="space-y-6">
          {/* Specialty Name Input (only for specialty uploads) */}
          {dataType === "specialty" && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Specialty Name
              </label>
              <input
                type="text"
                placeholder="e.g., Internal Medicine, Surgery, Pediatrics"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                disabled={isUploading}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Enter the exact name of the medical specialty</p>
            </div>
          )}

          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {dataType === "specialty" ? "CSV Data File" : "Excel Observerships File"}
            </label>
            
            <div
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
                dragActive
                  ? `${dataType === "specialty" ? "border-blue-400 bg-blue-50" : "border-purple-400 bg-purple-50"}`
                  : file
                  ? "border-green-400 bg-green-50"
                  : `border-gray-300 hover:${dataType === "specialty" ? "border-blue-400 bg-blue-50" : "border-purple-400 bg-purple-50"}`
              } ${isUploading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => !isUploading && document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                accept={dataType === "specialty" ? ".csv" : ".xlsx,.xls"}
                onChange={(e) => setFile(e.target.files[0])}
                disabled={isUploading}
                className="hidden"
              />
              
              {file ? (
                <div className="space-y-2">
                  <svg className="w-12 h-12 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    className="text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <svg className="w-12 h-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {dragActive ? `Drop ${dataType === "specialty" ? "CSV" : "Excel"} file here` : "Click to upload or drag and drop"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {dataType === "specialty" ? "CSV files only" : "Excel files (.xlsx, .xls)"}, max 10MB
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Upload Button */}
          <button
            type="submit"
            disabled={isUploading || (dataType === "specialty" && !specialty.trim()) || !file}
            className={`w-full bg-gradient-to-r ${
              dataType === "specialty" 
                ? "from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800" 
                : "from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
            } disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-4 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center justify-center`}
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Uploading File...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload {dataType === "specialty" ? "Specialty Data" : "Observerships Data"}
              </>
            )}
          </button>
        </form>

        {/* Message */}
        {message && (
          <div className={`mt-6 p-4 rounded-xl border ${
            message.includes("✅") 
              ? "bg-green-50 text-green-800 border-green-200" 
              : "bg-red-50 text-red-800 border-red-200"
          }`}>
            {message}
          </div>
        )}
      </div>

      {/* Upload Guidelines */}
      <div className={`${dataType === "specialty" ? "bg-blue-50 border-blue-200" : "bg-purple-50 border-purple-200"} rounded-2xl p-6 border`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className={`w-6 h-6 ${dataType === "specialty" ? "text-blue-600" : "text-purple-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-4">
            <h3 className={`text-lg font-semibold ${dataType === "specialty" ? "text-blue-900" : "text-purple-900"} mb-3`}>
              {dataType === "specialty" ? "Specialty Upload" : "Observership Upload"} Guidelines
            </h3>
            <div className={`text-sm ${dataType === "specialty" ? "text-blue-800" : "text-purple-800"} space-y-2`}>
              {dataType === "specialty" ? (
                <>
                  <div className="flex items-start">
                    <span className="font-medium mr-2">•</span>
                    <span>CSV files should contain program data with proper headers</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium mr-2">•</span>
                    <span>Specialty names should match existing categories when possible</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium mr-2">•</span>
                    <span>Uploading the same specialty name will update the existing entry</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start">
                    <span className="font-medium mr-2">•</span>
                    <span>Excel files should contain observership data with proper columns</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium mr-2">•</span>
                    <span>Include institution names, locations, specialties, fees, and requirements</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium mr-2">•</span>
                    <span>Uploading will replace the existing observerships database</span>
                  </div>
                </>
              )}
              <div className="flex items-start">
                <span className="font-medium mr-2">•</span>
                <span>File size should not exceed 10MB for optimal performance</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Specialty */}
      <div className="bg-white rounded-2xl border border-red-200 p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-red-700 mb-1">🗑️ Delete Specialty Data</h3>
        <p className="text-xs text-gray-500 mb-4">Removes all Firestore rows, metadata, Storage file, and local cache for a specialty.</p>
        <div className="flex gap-3">
          <input
            type="text"
            value={deleteSpecialty}
            onChange={e => { setDeleteSpecialty(e.target.value); setDeleteMessage(""); }}
            placeholder="Specialty name (e.g. Internal Medicine)"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          <button
            type="button"
            onClick={handleDeleteSpecialty}
            disabled={isDeleting || !deleteSpecialty.trim()}
            className="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </div>
        {deleteMessage && (
          <p className="mt-3 text-sm font-medium text-gray-700">{deleteMessage}</p>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
          <div className={`text-2xl font-bold ${dataType === "specialty" ? "text-blue-600" : "text-purple-600"} mb-1`}>
            {dataType === "specialty" ? "CSV" : "XLSX"}
          </div>
          <div className="text-sm text-gray-600">Supported Format</div>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
          <div className="text-2xl font-bold text-orange-600 mb-1">10MB</div>
          <div className="text-sm text-gray-600">Max File Size</div>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
          <div className="text-2xl font-bold text-green-600 mb-1">Auto</div>
          <div className="text-sm text-gray-600">Backup & Logging</div>
        </div>
      </div>
    </div>
  );
}

export default AdminUpload;